/**
 * @fileoverview Seção "Triagem" no detalhe do card do CRM: a última triagem do
 * card (status, progresso, itens e a OS) e o botão para abrir a conversa. Só
 * leitura e sem os dados do cliente: eles ficam no painel da conversa.
 */

import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { Check, Circle, MessageSquare, AlertTriangle, Minus } from "lucide-react";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import useTriageEnabled from "../../hooks/useTriageEnabled";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const text = dark ? "rgba(255,255,255,0.88)" : "#1f2937";
  const muted = dark ? "rgba(255,255,255,0.55)" : "#52525B";
  const border = dark ? "rgba(255,255,255,0.10)" : "#E4E4E7";
  return {
    box: { border: `1px solid ${border}`, borderRadius: 10, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10 },
    head: { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" },
    name: { fontSize: 13.5, fontWeight: 600, color: text },
    status: { fontSize: 12, fontWeight: 600, color: muted },
    attention: { fontSize: 12, color: dark ? "#fbbf24" : "#92400e", display: "flex", alignItems: "center", gap: 6 },
    bar: { height: 4, borderRadius: 2, background: border, overflow: "hidden" },
    barFill: { height: "100%", background: dark ? "rgba(255,255,255,0.7)" : "#3f3f46" },
    meta: { fontSize: 12, color: muted, display: "flex", gap: 12, flexWrap: "wrap" },
    items: { display: "flex", flexDirection: "column", gap: 4, margin: 0, padding: 0, listStyle: "none" },
    item: { display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, color: text },
    itemIcon: { display: "flex", color: muted, "& svg": { width: 13, height: 13 } },
    itemStatus: { marginLeft: "auto", fontSize: 11.5, color: muted, whiteSpace: "nowrap" },
    button: {
      alignSelf: "flex-start",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 12.5,
      fontWeight: 600,
      padding: "6px 10px",
      borderRadius: 8,
      border: `1px solid ${border}`,
      background: "transparent",
      color: text,
      cursor: "pointer",
      "& svg": { width: 14, height: 14 },
      "&:hover": { background: dark ? "rgba(255,255,255,0.06)" : "#F4F4F5" },
    },
    error: { fontSize: 12, color: muted },
  };
});

const IconeDoItem = ({ status }) => {
  if (status === "COMPLETED") return <Check aria-hidden="true" />;
  if (status === "WAIVED") return <Minus aria-hidden="true" />;
  if (status === "INVALID" || status === "WAITING_RESEND") return <AlertTriangle aria-hidden="true" />;
  return <Circle aria-hidden="true" />;
};

const LeadTriageSection = ({ leadId, className, titleClassName }) => {
  const classes = useStyles();
  const enabled = useTriageEnabled();
  const history = useHistory();
  const [estado, setEstado] = useState({ data: null, error: false });

  useEffect(() => {
    setEstado({ data: null, error: false });
    if (!enabled || !leadId) return undefined;
    let vivo = true;
    api
      .get(`/triage-sessions/by-lead/${leadId}`)
      .then(({ data }) => vivo && setEstado({ data: data || null, error: false }))
      .catch(() => vivo && setEstado({ data: null, error: true }));
    return () => {
      vivo = false;
    };
  }, [enabled, leadId]);

  if (!enabled || (!estado.data && !estado.error)) return null;
  const t = estado.data;
  const pct = t && t.progress?.total ? Math.round((t.progress.done / t.progress.total) * 100) : 0;

  return (
    <div className={className}>
      <span className={titleClassName} style={{ display: "block", marginBottom: 10 }}>
        {i18n.t("triage.crm.section")}
      </span>
      {!t ? (
        <div className={classes.error}>{i18n.t("triage.crm.loadError")}</div>
      ) : (
        <div className={classes.box} data-lead-triage>
          <div className={classes.head}>
            <span className={classes.name}>{t.definitionName || i18n.t("triage.crm.section")}</span>
            <span className={classes.status}>{i18n.t(`triage.sessionStatus.${t.status}`, { defaultValue: t.status })}</span>
          </div>
          {t.attentionReason && !["COMPLETED", "CANCELLED"].includes(t.status) && (
            <div className={classes.attention}>
              <AlertTriangle size={13} aria-hidden="true" />
              {i18n.t(`triage.attention.${t.attentionReason}`, { defaultValue: t.attentionReason })}
            </div>
          )}
          <div className={classes.bar} role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
            <div className={classes.barFill} style={{ width: `${pct}%` }} />
          </div>
          <div className={classes.meta}>
            <span>{i18n.t("triage.crm.progress", { done: t.progress?.done ?? 0, total: t.progress?.total ?? 0 })}</span>
            {t.workOrder?.number && <span>{i18n.t("triage.crm.workOrder", { number: t.workOrder.number })}</span>}
          </div>
          <ul className={classes.items}>
            {t.requirements.map((r) => (
              <li key={r.key} className={classes.item}>
                <span className={classes.itemIcon}>
                  <IconeDoItem status={r.status} />
                </span>
                <span>{r.name}</span>
                <span className={classes.itemStatus}>{i18n.t(`triage.requirementStatus.${r.status}`, { defaultValue: r.status })}</span>
              </li>
            ))}
          </ul>
          {t.ticket?.uuid && (
            <button type="button" className={classes.button} onClick={() => history.push(`/tickets/${t.ticket.uuid}`)}>
              <MessageSquare aria-hidden="true" />
              {i18n.t("triage.crm.openConversation")}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default LeadTriageSection;
